import React, { useState, useEffect } from 'react';
import {
  FaBook,
  FaEnvelope,
  FaUser,
  FaCalendar,
  FaCheck,
  FaClock,
  FaTimes,
  FaTrash,
  FaEye,
  FaSearch,
  FaShoppingCart,
  FaTruck,
  FaBan,
  FaChartBar,
  FaFilter,
  FaEdit
} from 'react-icons/fa';
import { apiGet, apiPatch, apiDelete } from "../../services/api";
import { useNotification } from '../../contexts/NotificationContext';

const AdminResupplyPanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updating, setUpdating] = useState(false);
  const { showNotification } = useNotification();

  // Статусы заказов
  const orderStatuses = {
    pending: { label: 'В ожидании', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: FaClock },
    approved: { label: 'Одобрено к покупке', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: FaCheck },
    rejected: { label: 'Отклонено', color: 'bg-red-100 text-red-800 border-red-200', icon: FaTimes },
    purchased: { label: 'Книга куплена', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: FaShoppingCart },
    delivered: { label: 'Доставлено в библиотеку', color: 'bg-green-100 text-green-800 border-green-200', icon: FaTruck },
    cancelled: { label: 'Отменено', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: FaBan }
  };

  // Загрузка заказов
  const loadOrders = async (page = 1, searchValue = searchTerm, filterValue = filter) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20
      };

      // Добавляем фильтр по статусу только если он не 'all'
      if (filterValue && filterValue !== 'all') {
        params.status = filterValue;
      }

      // Добавляем поиск только если есть текст
      if (searchValue && searchValue.trim()) {
        params.search = searchValue.trim();
      }

      console.log('Loading orders with params:', params); // Для отладки

      const response = await apiGet(`${process.env.REACT_APP_API_URL}/api/order`, { params });

      if (response.data) {
        setOrders(response.data.orders || []);
        setCurrentPage(response.data.pagination?.page || 1);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка статистики
  const loadStats = async () => {
    try {
      const response = await apiGet(`${process.env.REACT_APP_API_URL}/api/order/stats`);
      if (response.data) {
        setStats(response.data.stats || {});
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadOrders(1, searchTerm, filter); // Сбрасываем на первую страницу при изменении фильтров
    setCurrentPage(1);
  }, [filter, searchTerm]);

  useEffect(() => {
    loadOrders(currentPage, searchTerm, filter);
  }, [currentPage]);

  useEffect(() => {
    loadStats();
  }, []);

  // Обновление статуса заказа
  const updateOrderStatus = async (orderId, status, adminComment = '') => {
    if (!orderId || !status) return;

    setUpdating(true);
    try {
      const response = await apiPatch(`${process.env.REACT_APP_API_URL}/api/order/${orderId}`, {
        status,
        adminComment
      });

      if (response.status === 200) {
        await loadOrders(currentPage, searchTerm, filter);
        await loadStats();
        setSelectedOrder(null);

        // Показываем уведомление об успехе
        showNotification('Статус заказа успешно обновлен', 'success');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      showNotification('Ошибка при обновлении статуса', 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Удаление заказа
  const deleteOrder = async (orderId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот заказ?')) return;

    setUpdating(true);
    try {
      const response = await apiDelete(`${process.env.REACT_APP_API_URL}/api/order/${orderId}`);

      if (response.status === 200) {
        await loadOrders(currentPage, searchTerm, filter);
        await loadStats();
        setSelectedOrder(null);
        showNotification('Заказ успешно удален', 'success');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      showNotification('Ошибка при удалении заказа', 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Функция для показа уведомлений (removed local implementation)

  // Обработка поиска
  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setCurrentPage(1);
    loadOrders(1, searchTerm, filter);
  };

  // Обработка изменения фильтра
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    // loadOrders будет вызван автоматически через useEffect
  };

  // Обработка изменения поискового запроса
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    // Добавляем небольшую задержку для избежания слишком частых запросов
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      if (value !== searchTerm) return; // Проверяем, что значение не изменилось
      setCurrentPage(1);
    }, 300);
  };

  // Смена страницы
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    return orderStatuses[status] || orderStatuses.pending;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Заголовок */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-[var(--primary-color)] to-[var(--primary-light)] rounded-lg">
              <FaBook className="text-2xl text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Управление заказами на книги
              </h1>
              <p className="text-gray-600">
                Администрирование заказов на докомплектование библиотечного фонда
              </p>
            </div>
          </div>
        </div>

        {/* Статистика */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[var(--primary-color)] bg-opacity-10 rounded-lg">
                <FaChartBar className="text-[var(--primary-color)] text-lg" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-600">Всего</p>
                <p className="text-lg font-semibold text-gray-900">{stats.total || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FaClock className="text-yellow-600 text-lg" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-600">В ожидании</p>
                <p className="text-lg font-semibold text-gray-900">{stats.pending || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaCheck className="text-blue-600 text-lg" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-600">Одобрено</p>
                <p className="text-lg font-semibold text-gray-900">{stats.approved || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FaShoppingCart className="text-purple-600 text-lg" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-600">Куплено</p>
                <p className="text-lg font-semibold text-gray-900">{stats.purchased || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <FaTruck className="text-green-600 text-lg" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-600">Доставлено</p>
                <p className="text-lg font-semibold text-gray-900">{stats.delivered || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <FaTimes className="text-red-600 text-lg" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-600">Отклонено</p>
                <p className="text-lg font-semibold text-gray-900">{stats.rejected || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Фильтры и поиск */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Поиск */}
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Поиск по книге, автору или имени заказчика..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                />
              </div>
            </div>

            {/* Фильтр по статусу */}
            <div className="sm:w-48">
              <select
                value={filter}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                <option value="all">Все статусы</option>
                <option value="pending">В ожидании</option>
                <option value="approved">Одобрено к покупке</option>
                <option value="purchased">Книга куплена</option>
                <option value="delivered">Доставлено</option>
                <option value="rejected">Отклонено</option>
                <option value="cancelled">Отменено</option>
              </select>
            </div>
          </div>
        </div>

        {/* Список заказов */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary-color)]"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <FaBook className="mx-auto text-4xl text-gray-300 mb-4" />
              <p className="text-gray-500">Заказы не найдены</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Книга / Автор
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Заказчик
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Статус
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Действия
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => {
                      const statusInfo = getStatusInfo(order.status);
                      const StatusIcon = statusInfo.icon;

                      return (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{order.Book}</p>
                              <p className="text-sm text-gray-500">Автор: {order.Author}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{order.FullName}</p>
                              <p className="text-sm text-gray-500">{order.Email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {formatDate(order.createdAt)}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusInfo.color}`}>
                              <StatusIcon className="mr-1" />
                              {statusInfo.label}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setSelectedOrder(order)}
                                className="p-2 text-[var(--primary-color)] hover:bg-[var(--primary-color)] hover:bg-opacity-10 rounded-lg transition-colors"
                                title="Просмотреть детали"
                              >
                                <FaEye />
                              </button>
                              <button
                                onClick={() => deleteOrder(order.id)}
                                disabled={updating}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                                title="Удалить заказ"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Пагинация */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Страница {currentPage} из {totalPages}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Назад
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Вперед
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Модальное окно детального просмотра */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Блюр фон */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setSelectedOrder(null)}
          />

          {/* Модальное содержимое */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Заголовок */}
            <div className="bg-gradient-to-r from-[var(--primary-color)] to-[var(--primary-light)] p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <FaBook className="text-xl text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Детали заказа #{selectedOrder.id}</h2>
                    <p className="text-white text-opacity-80 text-sm">
                      {formatDate(selectedOrder.createdAt)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                >
                  <FaTimes className="text-white text-lg" />
                </button>
              </div>
            </div>

            {/* Содержимое */}
            <div className="p-6">
              {/* Информация о книге */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FaBook className="mr-2 text-[var(--primary-color)]" />
                  Информация о книге
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Название книги</label>
                    <p className="text-gray-900 font-medium">{selectedOrder.Book}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Автор</label>
                    <p className="text-gray-900">{selectedOrder.Author}</p>
                  </div>
                  {selectedOrder.Note && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Примечание от заказчика</label>
                      <p className="text-gray-900">{selectedOrder.Note}</p>
                    </div>
                  )}
                  {selectedOrder.adminComment && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Комментарий администратора</label>
                      <p className="text-gray-900">{selectedOrder.adminComment}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Информация о заказчике */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FaUser className="mr-2 text-[var(--primary-color)]" />
                  Информация о заказчике
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Полное имя</label>
                    <p className="text-gray-900">{selectedOrder.FullName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Email</label>
                    <p className="text-gray-900">{selectedOrder.Email}</p>
                  </div>
                </div>
              </div>

              {/* Текущий статус */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Текущий статус</h3>
                <div className="flex items-center space-x-3">
                  {(() => {
                    const statusInfo = getStatusInfo(selectedOrder.status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusInfo.color}`}>
                        <StatusIcon className="mr-2" />
                        {statusInfo.label}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* Действия */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Управление статусом</h3>

                {/* Быстрые действия в зависимости от текущего статуса */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  {selectedOrder.status === 'pending' && (
                    <>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'approved')}
                        disabled={updating}
                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        <FaCheck />
                        <span>Одобрить</span>
                      </button>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'rejected')}
                        disabled={updating}
                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        <FaTimes />
                        <span>Отклонить</span>
                      </button>
                    </>
                  )}

                  {selectedOrder.status === 'approved' && (
                    <>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'purchased')}
                        disabled={updating}
                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        <FaShoppingCart />
                        <span>Книга куплена</span>
                      </button>
                      <button
                        onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}
                        disabled={updating}
                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                      >
                        <FaBan />
                        <span>Отменить</span>
                      </button>
                    </>
                  )}

                  {selectedOrder.status === 'purchased' && (
                    <button
                      onClick={() => updateOrderStatus(selectedOrder.id, 'delivered')}
                      disabled={updating}
                      className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      <FaTruck />
                      <span>Доставлено</span>
                    </button>
                  )}
                </div>

                {/* Все доступные статусы */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Изменить статус на:</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(orderStatuses).map(([status, info]) => {
                      const StatusIcon = info.icon;
                      const isCurrentStatus = selectedOrder.status === status;

                      return (
                        <button
                          key={status}
                          onClick={() => updateOrderStatus(selectedOrder.id, status)}
                          disabled={updating || isCurrentStatus}
                          className={`flex items-center space-x-2 px-3 py-2 text-xs rounded-lg transition-colors ${isCurrentStatus
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          <StatusIcon />
                          <span>{info.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Кнопка удаления */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => deleteOrder(selectedOrder.id)}
                    disabled={updating || selectedOrder.status === 'delivered'}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaTrash />
                    <span>Удалить заказ</span>
                  </button>
                  {selectedOrder.status === 'delivered' && (
                    <p className="text-xs text-gray-500 mt-2">
                      Доставленные заказы нельзя удалить
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Индикатор загрузки при обновлении */}
      {updating && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--primary-color)]"></div>
            <span className="text-gray-700">Обновление...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResupplyPanel;